import { ENV } from "@/config/env";

export class AuthRequestError extends Error {
  constructor(message, status = 0, fields = {}, retryAfter = 0) {
    super(message);
    this.name = "AuthRequestError";
    Object.assign(this, { status, fields, retryAfter });
  }
}

export const readAuthError = (fields, fallback) => {
  const value = fields?.detail || fields?.non_field_errors || Object.values(fields || {})[0];
  return (Array.isArray(value) ? value[0] : value) || fallback;
};

// Authentication requests do not depend on an existing login session.
export async function requestAuth(path, body, { signal, timeoutMs = 15000, method = "POST", access } = {}) {
  const controller = new AbortController();
  let timedOut = false;
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  try {
    const response = await fetch(`${ENV.backendUrl}${path}`, {
      method,
      headers: { "Content-Type": "application/json", ...(access ? { Authorization: `Bearer ${access}` } : {}) },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: controller.signal,
    });
    let data = {};
    if (response.status !== 204 && response.status !== 205) {
      try { data = await response.json(); } catch (_) {
        throw new AuthRequestError("The server returned an unexpected response. Please try again.", response.status);
      }
    }
    if (!response.ok) {
      const retryAfter = Number(response.headers?.get("Retry-After")) || 0;
      const message = response.status === 429
        ? `Too many attempts. Please ${retryAfter ? `wait ${retryAfter} seconds and ` : ""}try again.`
        : response.status >= 500 ? "The service is temporarily unavailable. Please try again."
          : readAuthError(data, "The request failed. Please try again.");
      throw new AuthRequestError(message, response.status, data, retryAfter);
    }
    return data;
  } catch (error) {
    if (timedOut) throw new AuthRequestError("The request timed out. Please try again.");
    if (error.name === "AbortError" || error instanceof AuthRequestError) throw error;
    throw new AuthRequestError("Unable to connect. Please check your connection and try again.");
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", abort);
  }
}
